import React from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { LayoutDashboard, Folder, FileText, Image as ImageIcon, Download, MessageSquare, LogOut, ExternalLink } from 'lucide-react';
import { UKGMLogo } from '@/lib/ui';

const item = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? 'bg-ukgm-gold/15 text-ukgm-blue border border-ukgm-gold/40' : 'text-ukgm-slate hover:bg-ukgm-cream hover:text-ukgm-blue'
  }`;

export default function AdminLayout() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const handleLogout = async () => { await logout(); nav('/admin/login'); };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[260px_1fr] bg-ukgm-cream">
      <aside className="bg-white border-r border-ukgm-gold/25 md:min-h-screen p-5 flex flex-col" data-testid="admin-sidebar">
        <Link to="/admin" className="flex items-center gap-3 mb-8">
          <UKGMLogo className="w-11 h-11" />
          <div>
            <div className="font-serif text-ukgm-blue font-semibold text-sm leading-tight">Universal Krishna Gaukul Mission</div>
            <div className="text-[10px] tracking-widest uppercase text-ukgm-gold mt-0.5">Content Panel</div>
          </div>
        </Link>

        <nav className="flex-1 flex flex-col gap-1">
          <NavLink to="/admin" end className={item} data-testid="admin-nav-overview"><LayoutDashboard className="w-4 h-4" /> {t('admin.overview')}</NavLink>
          <NavLink to="/admin/projects" className={item} data-testid="admin-nav-projects"><Folder className="w-4 h-4" /> {t('admin.manage_projects')}</NavLink>
          <NavLink to="/admin/blogs" className={item} data-testid="admin-nav-blogs"><FileText className="w-4 h-4" /> {t('admin.manage_blogs')}</NavLink>
          <NavLink to="/admin/gallery" className={item} data-testid="admin-nav-gallery"><ImageIcon className="w-4 h-4" /> {t('admin.manage_gallery')}</NavLink>
          <NavLink to="/admin/media" className={item} data-testid="admin-nav-media"><Download className="w-4 h-4" /> {t('admin.manage_media')}</NavLink>
          <NavLink to="/admin/messages" className={item} data-testid="admin-nav-messages"><MessageSquare className="w-4 h-4" /> {t('admin.manage_messages')}</NavLink>
        </nav>

        <div className="pt-5 border-t border-ukgm-gold/20 mt-5">
          <div className="text-xs text-ukgm-slate">Signed in as</div>
          <div className="text-sm font-semibold text-ukgm-blue truncate">{user?.email}</div>
          <Link to="/" className="mt-3 flex items-center gap-2 text-xs text-ukgm-slate hover:text-ukgm-blue" data-testid="view-site-link">
            <ExternalLink className="w-3.5 h-3.5" /> View public site
          </Link>
          <button onClick={handleLogout} className="mt-3 w-full flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-ukgm-slate hover:bg-ukgm-cream" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> {t('admin.logout')}
          </button>
        </div>
      </aside>

      <main className="p-6 md:p-10 max-w-6xl w-full">
        <Outlet />
      </main>
    </div>
  );
}
